/**
 * 「会社情報取得」（縦串9、028、docs/23 §4）。会社名か法人番号を打って探し、候補を選んで「反映」で 会社名・フリガナ・〒・住所・法人番号 を入力欄に入れる。
 *   - サーバーの GET /corporate?number= ／ ?name=&pref= を呼ぶ（画面から国税庁を直接は呼ばない。控えが効く）
 *   - ID が無い（Me.features.corporateLookup=false）ときはこの部品を出さない
 *   - 出典（利用規約で必須）は「出典：国税庁 Web-API」と「？」＝NtaSource（2文の全文は「？」の窓。2026-10-13）
 *   - 2026-10-13：「株式会社 ○○」「(株)○○」「ＩＢＡＸ」のような打ち方もサーバーが整えてから国税庁へ（ntaNameQuery）。整えた名前は「『○○』で探しました」
 *   - 2026-10-13（射場さん）：Board の「会社選択」に合わせた＝候補を押しただけでは入力欄を変えない（「反映」で選んだ項目だけ）。候補が1件なら最初から選んだ状態
 *   - 2026-10-14（射場さん、Figma「会社情報取得（10/14）」②③）：窓は作る・直すシート（Sheet）＝頭は「取消｜会社情報取得｜反映」（反映は候補を選ぶまで押せない）。
 *     PC は真ん中の枠（幅 720px）、スマホ版は下から出るシート。中身は 検索の行（iOS の検索の欄・都道府県・「検索」）→ 候補（白いまとまり。選んだ行は ✓・太字・淡い藍の地）→
 *     入力欄に入る内容（5行：値とスイッチ。オフの行は取り消し線。住所の行の下に「登記どおり／短く」）→ 出典。題の下の説明の1行は候補の見出しの「？」へ
 */
import {
  NTA_ADDRESS_MODE_LABEL, NTA_ATTRIBUTION_LINES, NTA_MSG, NTA_SOURCE_LABEL, PREFECTURES, formatZip, ntaAddressOf,
  type CorporateInfo, type CorporateSearch, type NtaAddressMode,
} from "@genbaryoku/contract";
import { useEffect, useRef, useState, type CSSProperties } from "react";
import { api, ApiError, messageOf } from "../api.js";
import { CheckMark, GroupList, GroupSection } from "./GroupList.jsx";
import { BuildingIcon, ClearIcon, SearchIcon, UpDownIcon } from "./icons.jsx";
import { Segmented } from "./Segmented.jsx";
import { Sheet } from "./Sheet.jsx";
import { ErrorText, FieldAction, Help, Switch, guardComposingEnter } from "./ui.jsx";

import { ymdShort } from "../lib/dates.js";
/** 「会社情報取得」のボタンの名前と説明（顧客の登録・詳細の「名前」の欄の中の右端。docs/23 §4） */
export const CORPORATE_LOOKUP_LABEL = "会社情報取得";
export const CORPORATE_LOOKUP_TITLE = "国税庁の法人番号データから、会社名・住所・法人番号を取得します";

/** 「反映」で入力欄に入れる値。オンにした項目（と国税庁に値のある項目）だけが入る。building は住所を「短く」で入れて建物名を分けたときだけ */
export type CorporatePatch = { name?: string; nameKana?: string; postalCode?: string; address?: string; building?: string; corporateNumber?: string };

/** 反映する項目（並び＝画面の並び） */
const APPLY_ITEMS = [
  ["name", "会社名"],
  ["furigana", "フリガナ"],
  ["postalCode", "郵便番号"],
  ["address", "住所"],
  ["corporateNumber", "法人番号"],
] as const;
type ApplyItem = (typeof APPLY_ITEMS)[number][0];
const ALL_ON: Record<ApplyItem, boolean> = { name: true, furigana: true, postalCode: true, address: true, corporateNumber: true };

/** 候補の見出しの「？」（前は題の下の1行と題の「？」。2026-10-14 にここへまとめた） */
const CORPORATE_LOOKUP_HELP = "会社名（一部でも可）か法人番号（13桁）で検索し、候補を選んで「反映」を押すと、オンにした項目が入力欄に入ります。「株式会社」「（株）」や空白はあっても探せます。住所は「登記どおり」（2丁目2番1号）か「短く」（2-2-1。建物名は建物名の欄へ）で入ります。";

/** 「会社情報取得」のボタン（名前の欄の中の右端＝Field の end。2026-10-14 案A）。スマホ版・幅 767px 以下は建物の絵だけ（名前の欄の値と題を切らない） */
export function CorporateLookupButton({ onClick }: { onClick: () => void }) {
  return (
    <FieldAction icon={<BuildingIcon />} onClick={onClick} title={CORPORATE_LOOKUP_TITLE} narrowIcon testId="corp-open">
      {CORPORATE_LOOKUP_LABEL}
    </FieldAction>
  );
}

/**
 * 出典（国税庁 Web-API の利用規約で義務、docs/23 §4）。画面には「出典：国税庁 Web-API」と「？」だけを出し、
 * 「？」を押すと国税庁の指定の2文をそのまま窓に出す（言い換えない・要約しない。2026-10-13 射場さん）。
 * 「？」は Help＝押して開く・もう一度押すか外を押すか Esc で閉じる・キーボードでも開ける・aria-expanded／aria-controls。
 * 国税庁の機能が使えない会社（Me.features.corporateLookup=false）では使う側が出さない
 */
export function NtaSource({ style }: { style?: CSSProperties }) {
  return (
    <div className="hint" style={{ margin: 0, fontSize: 14, display: "flex", alignItems: "center", gap: 2, ...style }} data-testid="nta-source">
      {NTA_SOURCE_LABEL}
      <Help
        testId="help-nta-source"
        label="出典の全文"
        text={NTA_ATTRIBUTION_LINES.map((line, i) => (
          <span key={i} className="helpp">{line}</span>
        ))}
      />
    </div>
  );
}

const digits = (s: string) => s.replace(/[０-９]/g, (c) => String.fromCharCode(c.charCodeAt(0) - 0xfee0)).replace(/[^0-9]/g, "");
/** 画面の年月日（#320 ★8＝年2桁） */
const ymd = (d: string | null | undefined) => ymdShort(d);

export function CorporateLookupDialog({ open, initialQuery, onApply, onClose }: { open: boolean; initialQuery: string; onApply: (patch: CorporatePatch, c: CorporateInfo) => void; onClose: () => void }) {
  const [q, setQ] = useState(initialQuery);
  const [pref, setPref] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [result, setResult] = useState<CorporateSearch | null>(null);
  /** 選んだ候補（法人番号）。候補を押しただけでは入力欄を変えない */
  const [picked, setPicked] = useState<string | null>(null);
  const [checks, setChecks] = useState<Record<ApplyItem, boolean>>(ALL_ON);
  const [mode, setMode] = useState<NtaAddressMode>("registry");
  const inputRef = useRef<HTMLInputElement>(null);
  /** 検索の番号（閉じた・開き直した・続けて押した ときに前の答えを捨てる） */
  const seq = useRef(0);

  const show = (r: CorporateSearch | null) => {
    setResult(r);
    setPicked(r && r.items.length === 1 ? r.items[0]!.corporateNumber : null); // 1件なら最初から選んだ状態
  };
  /** prefCode＝絞る都道府県（開いたときの最初の検索は全国。state の pref はまだ前の値のことがある） */
  const search = async (query: string, prefCode = pref) => {
    const name = query.trim();
    const num = digits(name);
    setError(undefined);
    if (!name) {
      setError(NTA_MSG.nameTooShort);
      return;
    }
    const my = ++seq.current;
    setBusy(true);
    try {
      let r: CorporateSearch;
      if (/^[0-9０-９\-\s]+$/.test(name) && num.length === 13) {
        const c = await api<CorporateInfo>("GET", `/corporate?number=${num}`);
        r = { query: name, searchedAs: num, count: 1, items: [c], truncated: false };
      } else {
        r = await api<CorporateSearch>("GET", `/corporate?name=${encodeURIComponent(name)}${prefCode ? `&pref=${prefCode}` : ""}`);
      }
      if (my === seq.current) show(r);
    } catch (e) {
      if (my !== seq.current) return;
      show(null);
      setError(e instanceof ApiError && e.code === "CORPORATE_NOT_FOUND" ? NTA_MSG.corporateNotFound : messageOf(e));
    } finally {
      if (my === seq.current) setBusy(false);
    }
  };
  // 開くたびに最初の形へ（スイッチは全部オン・住所は登記どおり）。名前（か法人番号）が入っていればすぐ探す
  useEffect(() => {
    if (!open) {
      seq.current++;
      return;
    }
    setQ(initialQuery);
    setPref("");
    setBusy(false);
    setError(undefined);
    show(null);
    setChecks(ALL_ON);
    setMode("registry");
    if (initialQuery.trim().length >= 2) void search(initialQuery, "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const chosen = result?.items.find((c) => c.corporateNumber === picked) ?? null;
  const addr = chosen ? ntaAddressOf(chosen, mode) : null;
  /** 入力欄に入る値（国税庁に値が無い項目は null＝「—」） */
  const values: Record<ApplyItem, string | null> = {
    name: chosen?.name ?? null,
    furigana: chosen?.furigana || null,
    postalCode: chosen?.postalCode ? formatZip(chosen.postalCode) : null,
    address: addr?.address ? `${addr.address}${addr.building ? `　${addr.building}` : ""}` : null,
    corporateNumber: chosen?.corporateNumber ?? null,
  };
  /** 入るものが1つはある（値のある項目がオン） */
  const anyCheck = APPLY_ITEMS.some(([k]) => checks[k] && values[k]);
  /** 「反映」：オンにした項目だけを入力欄へ（国税庁に値の無い項目は入力欄を変えない） */
  const apply = () => {
    if (!chosen || !anyCheck) return;
    const a = ntaAddressOf(chosen, mode);
    const patch: CorporatePatch = {};
    if (checks.name) patch.name = chosen.name;
    if (checks.furigana && chosen.furigana) patch.nameKana = chosen.furigana;
    if (checks.postalCode && chosen.postalCode) patch.postalCode = chosen.postalCode;
    if (checks.address && a.address) {
      patch.address = a.address;
      if (a.building) patch.building = a.building;
    }
    if (checks.corporateNumber) patch.corporateNumber = chosen.corporateNumber;
    onApply(patch, chosen);
  };
  /** 国税庁に送った名前（空白・「株式会社」を外した・全角にした）。打った名前と同じ・法人番号のときは出さない */
  const searchedAs = result && result.searchedAs !== result.query.trim() && !/^[0-9]{13}$/.test(result.searchedAs) ? result.searchedAs : null;
  const prefName = PREFECTURES.find((p) => p.code === pref)?.name ?? "全国";

  return (
    <Sheet
      open={open}
      title={CORPORATE_LOOKUP_LABEL}
      cancelLabel="取消"
      doneLabel="反映"
      doneDisabled={!chosen || !anyCheck}
      onCancel={onClose}
      onDone={apply}
      className="corpsheet"
      testId="corp-sheet"
      doneTestId="corp-apply"
      cancelTestId="corp-cancel"
    >
      <div className="corpl">
        <div>
          <div className="corpq">
            <div className="corpq-box">
              <SearchIcon />
              <input
                ref={inputRef}
                className="corpq-in"
                type="text"
                enterKeyHint="search"
                autoComplete="off"
                value={q}
                placeholder="会社名 または 法人番号"
                aria-label="会社名 または 法人番号"
                onChange={(e) => setQ(e.target.value)}
                // Enter（スマホのキーボードの「検索」）は検索。preventDefault で Sheet の form（＝頭の「反映」）には届けない。変換確定の Enter は欄を動かさない
                onKeyDown={(e) => {
                  guardComposingEnter(e);
                  if (e.key === "Enter" && !(e.nativeEvent.isComposing || e.keyCode === 229)) {
                    e.preventDefault();
                    void search(q);
                  }
                }}
                data-testid="corp-q"
              />
              {q ? (
                <button
                  type="button"
                  className="corpq-clear"
                  aria-label="消去"
                  title="消去"
                  onClick={() => {
                    setQ("");
                    inputRef.current?.focus();
                  }}
                  data-testid="corp-q-clear"
                >
                  <ClearIcon />
                </button>
              ) : null}
            </div>
            <label className="corpq-pref">
              <span className="corpq-pref-tx" aria-hidden="true">{prefName}</span>
              <UpDownIcon />
              <select value={pref} onChange={(e) => setPref(e.target.value)} aria-label="都道府県で絞る" data-testid="corp-pref">
                <option value="">全国</option>
                {PREFECTURES.map((p) => (
                  <option key={p.code} value={p.code}>{p.name}</option>
                ))}
              </select>
            </label>
            <button type="button" className="btn corpq-go" disabled={busy} aria-busy={busy || undefined} onClick={() => void search(q)} data-testid="corp-search">
              検索
            </button>
          </div>
          <ErrorText>{error}</ErrorText>
        </div>

        <GroupSection
          className="corpl-sec"
          title={result ? `候補 ${result.count}件` : "候補"}
          titleTestId="corp-count"
          help={
            <>
              <Help testId="help-corporate" label="会社情報取得の説明" text={CORPORATE_LOOKUP_HELP} />
              {searchedAs ? (
                <span className="corpl-aside" data-testid="corp-searched">
                  「{searchedAs}」で探しました
                </span>
              ) : null}
            </>
          }
          note={result?.truncated ? `先頭の ${result.items.length} 件を出しています。${NTA_MSG.tooMany}` : undefined}
        >
          {busy && !result ? (
            <p className="hint corpl-none" role="status">検索中…</p>
          ) : result && result.items.length === 0 ? (
            <p className="hint corpl-none" data-testid="corp-none">「{result.searchedAs}」に合う法人は見つかりませんでした。名前を短くするか、都道府県を外してみてください。</p>
          ) : !result ? (
            <p className="hint corpl-none">会社名か法人番号で検索してください。</p>
          ) : (
            <GroupList plain className="corpl-cands">
              {result.items.map((c) => {
                const on = picked === c.corporateNumber;
                const a = ntaAddressOf(c, "registry");
                return (
                  <li key={c.corporateNumber} className={"grow corpl-cand" + (on ? " is-on" : "") + (c.closed ? " is-closed" : "")} data-testid={`corp-${c.corporateNumber}`}>
                    <button type="button" className="growin" aria-pressed={on} onClick={() => setPicked(c.corporateNumber)}>
                      <span className="corpl-cand-tx">
                        <span className="corpl-cand-nm">
                          <span className="corpl-nm">{c.name}</span>
                          {c.furigana ? <span className="corpl-kana">{c.furigana}</span> : null}
                          {c.closed ? (
                            <span className="corpl-closed" title={c.closeDate ? `閉鎖日 ${ymd(c.closeDate)}` : undefined}>
                              閉鎖
                            </span>
                          ) : null}
                        </span>
                        <span className="corpl-cand-addr" data-testid="corp-address">
                          {c.postalCode ? `〒${formatZip(c.postalCode)} ` : ""}
                          {a.address}
                        </span>
                        <span className="corpl-cand-meta">
                          法人番号 {c.corporateNumber}
                          {c.updateDate ? `　更新 ${ymd(c.updateDate)}` : ""}
                        </span>
                      </span>
                      <span className="corpl-cand-end">{on ? <CheckMark /> : null}</span>
                    </button>
                  </li>
                );
              })}
            </GroupList>
          )}
        </GroupSection>

        <GroupSection
          className="corpl-sec"
          title="入力欄に入る内容"
          help={<span className="corpl-aside corpl-offnote">オフにした項目は今のまま</span>}
        >
          <GroupList plain className="corpl-apply" data-testid="corp-apply-list">
            {APPLY_ITEMS.map(([k, label]) => {
              const v = values[k];
              const on = checks[k];
              // 行は <label> にしない（Switch が中に <label> を持つ）
              const row = (
                <div className="corpl-apply-top">
                  <span className="corpl-k">{label}</span>
                  <span className={"corpl-v" + (v && !on ? " is-off" : "") + (v ? "" : " is-none")} data-testid={`corp-value-${k}`}>
                    {v ?? "—"}
                  </span>
                  <Switch bare checked={on} disabled={!v} label={label} onChange={(x) => setChecks((c) => ({ ...c, [k]: x }))} testId={`corp-check-${k}`} />
                </div>
              );
              return (
                <li key={k} className={"grow corpl-apply-row" + (k === "address" ? " is-addr" : "")} data-testid={`corp-row-${k}`}>
                  {row}
                  {k === "address" ? (
                    <div className="corpl-mode">
                      <Segmented
                        options={(["registry", "short"] as NtaAddressMode[]).map((m) => ({ value: m, label: NTA_ADDRESS_MODE_LABEL[m] }))}
                        value={mode}
                        onChange={setMode}
                        label="住所の入れ方"
                        disabled={!chosen}
                        testId="corp-mode"
                      />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </GroupList>
        </GroupSection>

        <NtaSource />
      </div>
    </Sheet>
  );
}
